import { useState, useEffect, useRef } from '@wordpress/element';
import { addFilter } from '@wordpress/hooks';
import { createHigherOrderComponent } from '@wordpress/compose';
import { createElement, Fragment } from '@wordpress/element';
import { BiteStyleControl } from '@components/ExtendControls/ExtendControls';
import { nanoid } from 'nanoid';

// useSelect
import { useSelect } from '@wordpress/data';

import {
  TextControl,
  PanelBody,
  ToolbarGroup,
  ToolbarButton,
} from '@wordpress/components';

import {
  BlockControls,
  BlockEdit,
  useBlockProps,
  InspectorControls,
  getBlockHierarchyRootClientId,
} from '@wordpress/block-editor';

import { useBlockSupportStore } from '@components/Settings/BlockSupport/BlockSupportStore';
import { extendBlockProps } from '@core/extendBlockProps.js';
import BlockbiteControls from '@components/BlockbiteControls/BlockbiteControls';

import has from 'lodash/has';
import assign from 'lodash/assign';
import isObject from 'lodash/isObject';
import isEmpty from 'lodash/isEmpty';
import { divide } from 'lodash';

const allowedBlockbiteBlock = (blockname) => {
  const { disallowed } = useBlockSupportStore.getState();
  const isDisallowed = disallowed === null ? [] : disallowed;
  if (isDisallowed.includes(blockname)) {
    return false;
  } else {
    return true;
  }
};

const allowedBlockbiteExtraBlock = (blockname) => {
  const isAllowed = [
    'blockbite/advanced-button',
    'blockbite/group',
    'blockbite/section',
    'blockbite/canvas',
  ];
  return isAllowed.includes(blockname) ? true : false;
};

const hasBlockbiteAttributes = (attributes) => {
  if (
    has(attributes, 'biteStyle') ||
    has(attributes, 'biteClass') ||
    has(attributes, 'biteMotionStyle') ||
    has(attributes, 'biteMeta') ||
    has(attributes, 'biteMotionClass')
  ) {
    return true;
  } else {
    return false;
  }
};

const hasBlockbiteAttributesBlockbites = (attributes) => {
  if (has(attributes, 'bitesId')) {
    return true;
  } else {
    return false;
  }
};

const addBiteStyleAttribute = (block, name) => {
  if (block.name === 'core/template-part') {
    return block;
  } else {
    let updatedAttributes = assign({}, block.attributes);

    if (
      !hasBlockbiteAttributes(block.attributes) &&
      allowedBlockbiteBlock(name)
    ) {
      updatedAttributes = assign({}, updatedAttributes, {
        biteStyle: {
          type: 'array',
          default: [],
        },
        biteClass: {
          type: 'string',
          default: '',
        },
        biteMotionStyle: {
          type: 'array',
          default: [],
        },
        biteMotionClass: {
          type: 'string',
          default: '',
        },
        biteMeta: {
          type: 'object',
          default: {
            preset: true,
          },
        },
      });
    }
    // dynamic purposes
    if (block.name === 'core/query' || block.name === 'core/post-template') {
      updatedAttributes = assign({}, updatedAttributes, {
        biteParentName: {
          type: 'string',
          default: '',
        },
      });
    }

    return assign({}, block, { attributes: updatedAttributes });
  }
};

const addBiteStyleAttributeBlockbites = (block, name) => {
  if (block.name === 'core/template-part') {
    return block;
  } else {
    if (
      !hasBlockbiteAttributesBlockbites(block.attributes) &&
      allowedBlockbiteBlock(name)
    ) {
      return assign({}, block, {
        attributes: assign({}, block.attributes, {
          bitesId: {
            type: 'string',
            default: '',
          },
        }),
      });
    } else {
      return block;
    }
  }
};

// slightly different from addBiteStyleAttribute since name would return an array
const switchBiteStyleAttribute = (block) => {
  let switchBlock = block;

  if (
    hasBlockbiteAttributes(switchBlock.attributes) &&
    allowedBlockbiteBlock(switchBlock.name)
  ) {
    const attributes = ['biteStyle', 'biteMotionStyle'];
    attributes.forEach((attribute) => {
      // map to array if object is detected
      if (isObject(switchBlock.attributes[attribute])) {
        // check with lodash if object is empty
        if (!isEmpty(switchBlock.attributes[attribute])) {
          switchBlock.attributes[attribute] = map(
            switchBlock.attributes[attribute],
            (value, key) => {
              return { [key]: value };
            }
          );
        } else {
          switchBlock.attributes[attribute] = [];
        }
      }
    });
    /*
    const biteMetaAttributes = ['interactionStyle'];
    biteMetaAttributes.forEach((attribute) => {
      // map to array if object is detected
      if (isObject(switchBlock.biteMeta[attribute])) {
        // check with lodash if object is empty
        if (!isEmpty(switchBlock.biteMeta[attribute])) {
          switchBlock.biteMeta[attribute] = map(
            switchBlock.biteMeta[attribute],
            (value, key) => {
              return { [key]: value };
            }
          );
        } else {
          switchBlock.biteMeta[attribute] = [];
        }
      }
    });
    */

    return switchBlock;
  }
};

// hook for saving
function addBiteExtendPropsSave(props, block, attributes) {
  if (hasBlockbiteAttributes(attributes) && allowedBlockbiteBlock(block.name)) {
    const extendedBlockProps = extendBlockProps(props, attributes, true);

    return assign({}, props, {
      ...extendedBlockProps,
    });
  } else {
    return props;
  }
}

function checkParentBlockForRef(clientId) {
  const patternParent = wp.data
    .select('core/block-editor')
    .getBlockParentsByBlockName(clientId, 'core/block');
  if (patternParent.length) {
    const parentBlock = wp.data
      .select('core/block-editor')
      .getBlock(patternParent[0]);
    // check if any rootblock (upper nested block) has a ref
    if (parentBlock.attributes.ref) {
      return parentBlock.attributes.ref;
    }
  }
  return false;
}

// add biteStyle classname block props for use in editor
const withBiteStyleClassName = createHigherOrderComponent((BlockListBlock) => {
  return (props) => {
    const { attributes } = props;
    const extendedBlockProps = extendBlockProps(props, attributes);

    return <BlockListBlock {...extendedBlockProps} />;
  };
}, 'withBiteStyleClassName');

const withUniqueBiteId = (BlockEdit) => (props) => {
  const { attributes, clientId, setAttributes } = props;

  // Get editor blocks
  const editorBlocks = useSelect(
    (select) => select('core/block-editor').getBlocks(),
    []
  );

  // Blocks can have nested blocks under block.innerBlocks
  // This function will return all blocks including parent and nested blocks
  const getAllBlocks = (blocks) => {
    return blocks.flatMap((block) => {
      if (block.innerBlocks.length) {
        return [block, ...getAllBlocks(block.innerBlocks)];
      } else {
        return block;
      }
    });
  };

  const allBlocks = getAllBlocks(editorBlocks);

  useEffect(() => {
    if (!attributes.bitesId) {
      // Assign a unique ID if bitesId doesn't exist
      setAttributes({ bitesId: `bit_${nanoid(8)}` });
    } else {
      // Check for duplicates
      const duplicate = allBlocks.some(
        (block) =>
          block.attributes.bitesId === attributes.bitesId &&
          block.clientId !== clientId
      );

      if (duplicate) {
        const newBiteId = `bit_${nanoid(8)}`;
        const blocksWithSameId = allBlocks.filter(
          (block) => block.attributes.bitesId === attributes.bitesId
        );
        // Note that using something like a timestamp in the attributes won't work here
        // Because duplicating the block will also duplicate the timestamp
        const newerBlock = blocksWithSameId[blocksWithSameId.length - 1];

        // If this block is the newer block, assign a new unique ID
        if (newerBlock.clientId === clientId) {
          setAttributes({ bitesId: newBiteId });
        }
      }
    }
  }, [attributes.bitesId, allBlocks, setAttributes]);

  return <BlockEdit {...props} />;
};

// modify query block if included within swiper slider
const modifyQueryBlockForSwiper = (blockContent, block, attributes) => {
  const parentBlockName = attributes.biteParentName;
  if (
    block.name === 'core/query' &&
    parentBlockName === 'blockbite/dynamic-content-carousel'
  ) {
    return {
      ...blockContent,
      type: Fragment,
      props: {
        ...blockContent.props,
        children: createElement(Fragment, {}, blockContent.props.children),
      },
    };
  }
  return blockContent;
};

export const initBlockExtend = () => {
  // add biteStyle attribute to block
  addFilter(
    'blocks.registerBlockType',
    'blockbite/extend-block-attributes',
    addBiteStyleAttribute,
    99
  );

  // add biteStyle controls to block
  addFilter(
    'editor.BlockEdit',
    'blockbite/extend-block-props',
    BiteStyleControl,
    100
  );

  // add biteStyle className to block within editor
  addFilter(
    'editor.BlockListBlock',
    'blockbite/extend-block-list-props',
    withBiteStyleClassName,
    101
  );

  // add bitestyle className to block within save
  addFilter(
    'blocks.getSaveContent.extraProps',
    'blockbite/extend-block-save-props',
    addBiteExtendPropsSave,
    102
  );

  // when switching block
  addFilter(
    'blocks.switchToBlockType.transformedBlock',
    'blockbite/extend-block-attributes',
    switchBiteStyleAttribute,
    103
  );

  // modify query block if included within swiper slider
  addFilter(
    'blocks.getSaveElement',
    'blockbite/dynamic-content-carousel',
    modifyQueryBlockForSwiper,
    104
  );

  if (blockbite.data.postType === 'blockbites') {
    addFilter(
      'blocks.registerBlockType',
      'blockbite/extend-block-attributes-blockbites',
      addBiteStyleAttributeBlockbites,
      105
    );
    addFilter(
      'editor.BlockEdit',
      'blockbite/unique-block-id-blockbites',
      withUniqueBiteId,
      106
    );
  }
};

export default {
  initBlockExtend,
};
